import { useState, useEffect, useCallback } from "react";

declare global {
	interface Window {
		dataLayer: any[];
		gtag?: (...args: any[]) => void;
	}
}

export type ConsentType = "analytics" | "marketing";

interface ConsentState {
	analytics: boolean | null;
	marketing: boolean | null;
	hasResponded: boolean;
}

const STORAGE_KEY = "cookie-consent-v1";

export function useConsent() {
	const [consent, setConsent] = useState<ConsentState>(() => {
		if (typeof window === "undefined") {
			return { analytics: null, marketing: null, hasResponded: false };
		}

		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			try {
				return JSON.parse(stored) as ConsentState;
			} catch {
				// Corrupt stored value — fall back to the default rather than
				// throwing during the useState initializer (which crashes render).
			}
		}
		return { analytics: null, marketing: null, hasResponded: false };
	});

	useEffect(() => {
		if (typeof window === "undefined") return;

		window.dataLayer = window.dataLayer || [];
		function gtag(...args: any[]) {
			window.dataLayer.push(args);
		}

		gtag("consent", "default", {
			analytics_storage: "denied",
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			wait_for_update: 500,
		});
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (!window.gtag) return;
		if (!consent.hasResponded) return;

		window.gtag("consent", "update", {
			analytics_storage: consent.analytics ? "granted" : "denied",
			ad_storage: consent.marketing ? "granted" : "denied",
			ad_user_data: consent.marketing ? "granted" : "denied",
			ad_personalization: consent.marketing ? "granted" : "denied",
		});
	}, [consent]);

	const updateConsent = useCallback(
		(updates: Partial<ConsentState>) => {
			const newConsent = { ...consent, ...updates };
			setConsent(newConsent);
			localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent));
		},
		[consent]
	);

	const acceptAll = useCallback(() => {
		updateConsent({
			analytics: true,
			marketing: true,
			hasResponded: true,
		});
	}, [updateConsent]);

	const rejectAll = useCallback(() => {
		updateConsent({
			analytics: false,
			marketing: false,
			hasResponded: true,
		});
	}, [updateConsent]);

	return {
		consent,
		hasResponded: consent.hasResponded,
		analyticsGranted: consent.analytics === true,
		updateConsent,
		acceptAll,
		rejectAll,
	};
}
